'use client'
import { useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { useParams } from 'next/navigation'
import { useQuery } from '@tanstack/react-query'
import { ChevronRight, ChevronLeft } from 'lucide-react'
import { apiGetAllNews } from '@/services/newsHome/api'

// --- DỮ LIỆU CỐ ĐỊNH (Giữ nguyên để tránh lỗi nếu có reference khác, dù hiện tại chưa dùng) ---
const steps = [
    {
        number: 1,
        title: 'Giải pháp tài chính toàn diện',
        description: 'Chúng tôi không mang đến những giải pháp có sẵn. Mỗi kế hoạch tài chính là một cuộc đối thoại sâu sắc với tham vọng của bạn, nhằm hiện thực hóa những kết quả có thể chạm được.'
    },
    {
        number: 2,
        title: 'Xây dựng bởi doanh nhân, kiến tạo cho doanh nghiệp',
        description: 'Chúng tôi hiểu bạn hơn ai hết, vì chúng tôi từng ở vị trí của bạn.'
    },
    {
        number: 3,
        title: 'Không chỉ tư vấn, chúng tôi đồng hành',
        description: 'Chúng tôi không mang đến những giải pháp có sẵn. Mỗi kế hoạch tài chính là một cuộc đối thoại sâu sắc với tham vọng của bạn, nhằm hiện thực hóa những kết quả có thể chạm được.'
    },
    {
        number: 4,
        title: 'Kết nối vươn tầm, Thành công vượt bậc',
        description: 'Mạng lưới đối tác tài chính dày dặn của chúng tôi dẫn lối bạn đến với những cơ hội mà không ai khác có được.'
    }
]

const stats = [
    { number: '45,430', unit: 'Doanh nghiệp', description: 'Tổng số vốn vay huy động' },
    { number: '4', unit: '', description: 'Dự án gọi vốn thành công' },
    { number: '3', unit: '', description: 'Quỹ đầu tư đã kết nối' },
    { number: '9', unit: 'Tháng', description: 'Khởi động tuyến đến giải ngân' },
    { number: '11,375', unit: 'Tỷ VNĐ', description: 'Giá trị giao dịch trung bình' },
    { number: '17', unit: 'Năm', description: 'Kỳ hạn vay trung bình' }
]

// --- CẤU HÌNH CHO PHẦN TIN TỨC ---
const ITEMS_PER_PAGE = 3;

interface Activity {
    id: string;
    title: string;
    image: string;
    detailsLink: string;
    newsTypeName?: string;
}

interface DifferencesProps {
    companyId?: string | number;
}

export default function Differences({ companyId }: DifferencesProps) {
    const params = useParams();
    const finalId = companyId ? String(companyId) : (params.id as string);
    const [currentPage, setCurrentPage] = useState(1);

    const { data: newsData, isLoading } = useQuery({
        queryKey: ['activities', finalId],
        queryFn: async () => {
            const res = await apiGetAllNews({
                keywords: '',
                newsTypeId: Number(finalId),
                isPublic: '',
                page: 1,
                pageSize: 50,
            });
            return res?.isOK ? res.result.items : [];
        },
        enabled: !!finalId && !isNaN(Number(finalId)),
        staleTime: 5 * 60 * 1000,
    });


    const detailItem = newsData?.find((item: any) => item.seoDescription === 'Chi tiết');


    const allActivities: Activity[] = isLoading || !newsData?.length
        ? []
        : newsData
            .filter((item: any) => item.seoDescription === 'Hoạt động và sự kiện')
            .map((item: any) => ({
                id: item.id.toString(),
                title: item.title,
                image: item.image || '',
                newsTypeName: item.newsType?.title,
                detailsLink: item.url || '#',
            }));

    if (!detailItem && allActivities.length === 0) {
        return null;
    }

    const totalPages = Math.max(1, Math.ceil(allActivities.length / ITEMS_PER_PAGE));
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const currentActivities = allActivities.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    const displayActivities = currentActivities;

    const goToPage = (page: number) => {
        if (page >= 1 && page <= totalPages) setCurrentPage(page);
    };

    return (
        <div className="bg-[#EFEFEF] text-[#FFFFFF]" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>


            {detailItem && (
                <div className="w-full text-black">
                    <div className="mx-auto w-full px-4 py-8 md:py-16">
                        <div
                            className="w-full prose max-w-none [&_img]:max-w-full [&_img]:!h-auto [&_img]:mx-auto [&_img]:rounded-lg [&_iframe]:max-w-full [&_iframe]:w-full"
                            dangerouslySetInnerHTML={{ __html: detailItem.content || detailItem.description || '' }}
                        />
                    </div>
                </div>
            )}

            {allActivities.length > 0 && (
                <div className="w-full pt-10 pb-10 xl:pt-20 xl:pb-16">
                    <div className="mx-auto max-w-[62rem] px-4">
                        <div className="text-center mb-12">
                            <h2 className="text-3xl font-medium text-[#2D3746] mb-3">
                                Hoạt động và sự kiện
                            </h2>
                            <p className="text-[#2D3746] max-w-2xl mx-auto text-sm leading-relaxed" style={{ fontFamily: '"Helvetica Neue", Roboto, sans-serif' }}>
                                {allActivities[0]?.newsTypeName} thường xuyên tổ chức các hoạt động và sự kiện nổi bật, kết nối nhà đầu tư với những cơ hội phát triển bền vững
                            </p>
                        </div>

                        <div className="mb-8 grid grid-cols-1 gap-8 sm:grid-cols-3 items-start">
                            {displayActivities.map((item) => (
                                <Link
                                    key={item.id}
                                    href={`/news/${item.id}`}
                                    className="group flex flex-col cursor-pointer"
                                >
                                    <div className="relative w-full h-52 overflow-hidden rounded-lg mb-4">
                                        <Image
                                            src={item.image || '/placeholder-activity.jpg'}
                                            alt={item.title}
                                            fill
                                            className="object-cover transition-transform duration-500 group-hover:scale-105"
                                            unoptimized={true}
                                        />
                                    </div>

                                    <h3 className="text-lg font-medium text-[#2D3746] leading-snug group-hover:text-[#006633] transition-colors line-clamp-2" title={item.title}>
                                        {item.title}
                                    </h3>
                                </Link>
                            ))}
                        </div>

                        {totalPages > 1 && (
                            <div className="flex justify-center items-center gap-1.5 mt-8">
                                <button
                                    onClick={() => goToPage(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="group flex h-6 w-6 items-center justify-center rounded-full border border-black bg-white text-black transition-all hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed disabled:border-gray-300"
                                >
                                    <ChevronLeft className="h-2.5 w-2.5" strokeWidth={2} />
                                </button>

                                <div className="flex items-center gap-0.5">
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                        <button
                                            key={page}
                                            onClick={() => goToPage(page)}
                                            className={`flex h-6 w-6 items-center justify-center rounded-full text-[9px] font-bold transition-all
                                    ${page === currentPage
                                                    ? 'bg-[#006633] text-white'
                                                    : 'bg-transparent text-gray-500 hover:text-[#006633]'
                                                }`}
                                        >
                                            {page}
                                        </button>
                                    ))}
                                </div>

                                <button
                                    onClick={() => goToPage(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="group flex h-6 w-6 items-center justify-center rounded-full border border-black bg-white text-black transition-all hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed disabled:border-gray-300"
                                >
                                    <ChevronRight className="h-2.5 w-2.5" strokeWidth={2} />
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}